import {
  InputState,
  InputEvent,
  InputCallback,
  InputEventType,
  PointerState,
  Vec2,
} from '../types';
import { INPUT_CONFIG } from '../constants';

/**
 * Unified input manager for mouse, touch, and keyboard.
 * Handles cross-platform input with gesture recognition.
 */
export class InputManager {
  private state: InputState;
  private listeners: Map<InputEventType, Set<InputCallback>>;
  private container: HTMLElement;
  private longPressTimer: number | null = null;
  private lastTapTime: number = 0;
  private isMobile: boolean;

  constructor(container: HTMLElement) {
    this.container = container;
    this.isMobile = this.detectMobile();

    this.state = this.createInitialState();
    this.listeners = new Map();

    this.bindEvents();
  }

  private detectMobile(): boolean {
    return (
      (window as any).IS_MOBILE_APP ||
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0
    );
  }

  private createInitialState(): InputState {
    return {
      keys: new Set(),
      keysJustPressed: new Set(),
      keysJustReleased: new Set(),
      pointers: new Map(),
      primaryPointer: null,
      isDragging: false,
      dragDelta: { x: 0, y: 0 },
      isPinching: false,
      pinchScale: 1,
      pinchCenter: { x: 0, y: 0 },
      scrollDelta: 0,
      screenSize: {
        x: window.innerWidth,
        y: window.innerHeight,
      },
    };
  }

  private bindEvents(): void {
    // Pointer events (unified mouse/touch)
    this.container.addEventListener('pointerdown', this.onPointerDown);
    this.container.addEventListener('pointermove', this.onPointerMove);
    this.container.addEventListener('pointerup', this.onPointerUp);
    this.container.addEventListener('pointercancel', this.onPointerUp);
    this.container.addEventListener('pointerleave', this.onPointerUp);

    // Wheel events
    this.container.addEventListener('wheel', this.onWheel, { passive: false });

    // Keyboard events
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);

    // Prevent context menu on right-click
    this.container.addEventListener('contextmenu', (e) => e.preventDefault());

    // Resize
    window.addEventListener('resize', this.onResize);

    // Prevent default touch behaviors
    this.container.addEventListener(
      'touchstart',
      (e) => e.preventDefault(),
      { passive: false }
    );
  }

  private onPointerDown = (e: PointerEvent): void => {
    e.preventDefault();

    const pointer: PointerState = {
      id: e.pointerId,
      position: { x: e.clientX, y: e.clientY },
      startPosition: { x: e.clientX, y: e.clientY },
      delta: { x: 0, y: 0 },
      isDown: true,
      startTime: performance.now(),
    };

    this.state.pointers.set(e.pointerId, pointer);

    // Set primary pointer
    if (this.state.pointers.size === 1) {
      this.state.primaryPointer = pointer;
      this.startLongPressTimer(pointer);
    }

    // Check for pinch start
    if (this.state.pointers.size === 2) {
      this.cancelLongPressTimer();
      this.state.isPinching = true;
      this.state.pinchScale = 1;
      this.state.pinchCenter = this.getPinchCenter();
      this.emit({ type: 'pinchstart', pinchCenter: this.state.pinchCenter });
    }

    this.emit({ type: 'pointerdown', pointer, position: pointer.position });
  };

  private onPointerMove = (e: PointerEvent): void => {
    const pointer = this.state.pointers.get(e.pointerId);
    if (!pointer) return;

    const prevPosition = { ...pointer.position };
    pointer.position = { x: e.clientX, y: e.clientY };
    pointer.delta = {
      x: pointer.position.x - prevPosition.x,
      y: pointer.position.y - prevPosition.y,
    };

    // Check if we should start dragging
    if (pointer.isDown && !this.state.isDragging) {
      const distFromStart = Math.hypot(
        pointer.position.x - pointer.startPosition.x,
        pointer.position.y - pointer.startPosition.y
      );

      if (distFromStart > INPUT_CONFIG.dragThreshold) {
        this.state.isDragging = true;
        this.cancelLongPressTimer();
      }
    }

    // Update drag delta
    if (this.state.isDragging && this.state.primaryPointer) {
      this.state.dragDelta = {
        x: this.state.primaryPointer.position.x - this.state.primaryPointer.startPosition.x,
        y: this.state.primaryPointer.position.y - this.state.primaryPointer.startPosition.y,
      };
    }

    // Handle pinch gesture
    if (this.state.isPinching && this.state.pointers.size === 2) {
      const pointers = Array.from(this.state.pointers.values());
      const currentDist = this.getPointerDistance(pointers[0], pointers[1]);
      const startDist = this.getStartPointerDistance(pointers[0], pointers[1]);

      if (startDist > 0) {
        this.state.pinchScale = currentDist / startDist;
      }
      this.state.pinchCenter = this.getPinchCenter();

      this.emit({
        type: 'pinchmove',
        pinchScale: this.state.pinchScale,
        pinchCenter: this.state.pinchCenter,
      });
    }

    this.emit({ type: 'pointermove', pointer, position: pointer.position });
  };

  private onPointerUp = (e: PointerEvent): void => {
    const pointer = this.state.pointers.get(e.pointerId);
    if (!pointer) return;

    pointer.isDown = false;
    this.cancelLongPressTimer();

    // Check for tap
    const duration = performance.now() - pointer.startTime;
    const distance = Math.hypot(
      pointer.position.x - pointer.startPosition.x,
      pointer.position.y - pointer.startPosition.y
    );

    if (duration < 200 && distance < INPUT_CONFIG.dragThreshold) {
      // Check for double tap
      const timeSinceLastTap = performance.now() - this.lastTapTime;
      if (timeSinceLastTap < INPUT_CONFIG.doubleTapInterval) {
        this.emit({ type: 'tap', pointer, position: pointer.position });
      }
      this.lastTapTime = performance.now();
      this.emit({ type: 'tap', pointer, position: pointer.position });
    }

    // End pinch if we're going below 2 pointers
    if (this.state.isPinching && this.state.pointers.size <= 2) {
      this.state.isPinching = false;
      this.state.pinchScale = 1;
      this.emit({ type: 'pinchend' });
    }

    this.state.pointers.delete(e.pointerId);

    if (this.state.pointers.size === 0) {
      this.state.isDragging = false;
      this.state.dragDelta = { x: 0, y: 0 };
      this.state.primaryPointer = null;
    } else {
      // Set new primary pointer
      this.state.primaryPointer = this.state.pointers.values().next().value ?? null;
    }

    this.emit({ type: 'pointerup', pointer, position: pointer.position });
  };

  private onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    this.state.scrollDelta = e.deltaY;
    this.emit({ type: 'wheel', scrollDelta: e.deltaY });
  };

  private onKeyDown = (e: KeyboardEvent): void => {
    // Ignore if typing in an input
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
      return;
    }

    if (!this.state.keys.has(e.code)) {
      this.state.keysJustPressed.add(e.code);
    }
    this.state.keys.add(e.code);

    this.emit({ type: 'keydown', key: e.code });
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    this.state.keys.delete(e.code);
    this.state.keysJustReleased.add(e.code);

    this.emit({ type: 'keyup', key: e.code });
  };

  private onResize = (): void => {
    this.state.screenSize = {
      x: window.innerWidth,
      y: window.innerHeight,
    };
  };

  private startLongPressTimer(pointer: PointerState): void {
    this.longPressTimer = window.setTimeout(() => {
      if (!this.state.isDragging && pointer.isDown) {
        this.emit({ type: 'longpress', pointer, position: pointer.position });
      }
    }, INPUT_CONFIG.longPressDuration);
  }

  private cancelLongPressTimer(): void {
    if (this.longPressTimer !== null) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
  }

  private getPointerDistance(p1: PointerState, p2: PointerState): number {
    return Math.hypot(
      p1.position.x - p2.position.x,
      p1.position.y - p2.position.y
    );
  }

  private getStartPointerDistance(p1: PointerState, p2: PointerState): number {
    return Math.hypot(
      p1.startPosition.x - p2.startPosition.x,
      p1.startPosition.y - p2.startPosition.y
    );
  }

  private getPinchCenter(): Vec2 {
    const pointers = Array.from(this.state.pointers.values());
    if (pointers.length < 2) return { x: 0, y: 0 };

    return {
      x: (pointers[0].position.x + pointers[1].position.x) / 2,
      y: (pointers[0].position.y + pointers[1].position.y) / 2,
    };
  }

  private emit(event: InputEvent): void {
    const callbacks = this.listeners.get(event.type);
    if (callbacks) {
      callbacks.forEach((cb) => cb(event));
    }
  }

  // Public API

  public on(type: InputEventType, callback: InputCallback): void {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set());
    }
    this.listeners.get(type)!.add(callback);
  }

  public off(type: InputEventType, callback: InputCallback): void {
    const callbacks = this.listeners.get(type);
    if (callbacks) {
      callbacks.delete(callback);
    }
  }

  public getState(): InputState {
    return this.state;
  }

  public isKeyDown(key: string | string[]): boolean {
    const keys = Array.isArray(key) ? key : [key];
    return keys.some((k) => this.state.keys.has(k));
  }

  public isKeyJustPressed(key: string | string[]): boolean {
    const keys = Array.isArray(key) ? key : [key];
    return keys.some((k) => this.state.keysJustPressed.has(k));
  }

  public getPointerNDC(): Vec2 | null {
    if (!this.state.primaryPointer) return null;

    return {
      x: (this.state.primaryPointer.position.x / this.state.screenSize.x) * 2 - 1,
      y: -(this.state.primaryPointer.position.y / this.state.screenSize.y) * 2 + 1,
    };
  }

  public update(): void {
    // Clear frame-specific states
    this.state.keysJustPressed.clear();
    this.state.keysJustReleased.clear();
    this.state.scrollDelta = 0;

    // Reset pointer deltas
    this.state.pointers.forEach((pointer) => {
      pointer.delta = { x: 0, y: 0 };
    });
  }

  public destroy(): void {
    this.container.removeEventListener('pointerdown', this.onPointerDown);
    this.container.removeEventListener('pointermove', this.onPointerMove);
    this.container.removeEventListener('pointerup', this.onPointerUp);
    this.container.removeEventListener('pointercancel', this.onPointerUp);
    this.container.removeEventListener('pointerleave', this.onPointerUp);
    this.container.removeEventListener('wheel', this.onWheel);
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('resize', this.onResize);

    this.cancelLongPressTimer();
    this.listeners.clear();
  }
}
